import { FC, useState, useMemo } from 'react';
import { Box, Modal, Typography, Avatar, List, ListItem, ListItemIcon, ListItemText, IconButton } from '@mui/material';
import { CloseIcon } from '../../../../../components/Icons/CloseIcon';

import styles from './AddTravelBuddiesModal.module.scss';
import { TravelBuddy } from '../../../../../types/trip.types';
import { useGetTravelBuddiesQuery } from '../../../../../store/services/userContactsApi';
import CustomInput from '../../../../../common/CustomInput';
import CustomButton from '../../../../../common/CustomButton';
import { SearchIcon } from '../../../../../components/Icons';
import CustomCheckbox from '../../../../../common/CustomCheckbox';
import { buildAvatarUrl, generateInitials } from '../../../../../utils/avatarUtils';
import { ArrowBackIcon } from '../../../../../components/Icons/ArrowBackIcon';

type AddTravelBuddiesModalProps = {
  open: boolean;
  onClose: () => void;
  selectedBuddies: TravelBuddy[];
  onBuddiesChange: (buddies: TravelBuddy[]) => void;
  onConfirm?: (buddies: TravelBuddy[]) => void | Promise<void>;
  showBackButton?: boolean;
};

const AddTravelBuddiesModal: FC<AddTravelBuddiesModalProps> = ({
  open,
  onClose,
  selectedBuddies,
  onBuddiesChange,
  onConfirm,
  showBackButton = true,
}) => {
  const { data: travelBuddies, isLoading } = useGetTravelBuddiesQuery();
  const [searchQuery, setSearchQuery] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const filteredBuddies = useMemo(() => {
    if (!travelBuddies) return [];

    return travelBuddies.filter(buddy => {
      const fullName = `${buddy.first_name} ${buddy.last_name}`.toLowerCase();
      const username = buddy.username.toLowerCase();
      const email = buddy.email.toLowerCase();
      const query = searchQuery.toLowerCase();

      return fullName.includes(query) || username.includes(query) || email.includes(query);
    });
  }, [travelBuddies, searchQuery]);

  const handleBuddyToggle = (buddy: TravelBuddy) => {
    const isSelected = selectedBuddies.some(selected => selected.id === buddy.id);

    if (isSelected) {
      const updatedBuddies = selectedBuddies.filter(selected => selected.id !== buddy.id);
      onBuddiesChange(updatedBuddies);
    } else {
      onBuddiesChange([...selectedBuddies, buddy]);
    }
  };

  const handleAddBuddies = async () => {
    if (onConfirm) {
      try {
        setIsSubmitting(true);
        await onConfirm(selectedBuddies);
        onClose();
      } finally {
        setIsSubmitting(false);
      }
    } else {
      onClose();
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const getBuddyDisplayName = (buddy: TravelBuddy) => {
    if (buddy.first_name || buddy.last_name) {
      return `${buddy.first_name} ${buddy.last_name}`.trim();
    }
    return buddy.username;
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="add-travel-buddies-modal"
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(34, 43, 58, 0.2)',
        },
      }}
    >
      <Box className={styles.modalContainer}>
        <Box className={styles.modalHeader}>
          {showBackButton ? (
            <IconButton
              onClick={onClose}
              className={styles.backButton}
            >
              <ArrowBackIcon />
            </IconButton>
          ) : (
            <Box className={styles.headerSpacer} />
          )}

          <Typography
            className={styles.header}
            variant="h5Bold"
            sx={{ color: '#222B3A' }}
          >
            Add Travel Buddies
          </Typography>

          <IconButton
            onClick={onClose}
            className={styles.closeButton}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box className={styles.modalContent}>
          <Box className={styles.contentWrapper}>
            <Typography
              variant="h9Bold"
              className={styles.sectionTitle}
            >
              Travel Buddies
            </Typography>
            <Box className={styles.searchContainer}>
              <CustomInput
                inputName="search"
                inputLabel=""
                placeholder="Search"
                value={searchQuery}
                onChange={handleSearchChange}
                startIcon={
                  <SearchIcon
                    size={20}
                    color="#5E6A81"
                  />
                }
                customClass={styles.searchInput}
              />
            </Box>
            <Box className={styles.buddiesList}>
              {isLoading ? (
                <Typography>Loading travel buddies...</Typography>
              ) : filteredBuddies.length === 0 ? (
                <Box sx={{ padding: '24px' }}>
                  <Typography
                    variant="h10"
                    sx={{ color: '#5E6A81', textAlign: 'center', py: 2 }}
                  >
                    {searchQuery ? 'No buddies found matching your search' : 'No travel buddies available'}
                  </Typography>
                </Box>
              ) : (
                <List>
                  {filteredBuddies.map(buddy => {
                    const isSelected = selectedBuddies.some(selected => selected.id === buddy.id);
                    const avatarUrl = buildAvatarUrl(buddy.profile_picture);

                    return (
                      <ListItem
                        key={buddy.id}
                        onClick={() => handleBuddyToggle(buddy)}
                        className={styles.buddyItem}
                      >
                        <ListItemIcon>
                          <Avatar
                            src={avatarUrl || undefined}
                            alt=""
                            className={styles.buddyAvatar}
                            sx={{
                              width: 50,
                              height: 50,
                              backgroundColor: avatarUrl ? 'transparent' : '#e3f2fd',
                              color: avatarUrl ? 'transparent' : '#0098f0',
                              fontSize: '16px',
                              fontWeight: 600,
                            }}
                          >
                            {!avatarUrl &&
                              generateInitials(buddy.first_name, buddy.last_name, buddy.email, buddy.username)}
                          </Avatar>
                        </ListItemIcon>

                        <ListItemText>
                          <Typography
                            variant="h9Semibold"
                            sx={{ color: '#222B3A' }}
                          >
                            {getBuddyDisplayName(buddy)}
                          </Typography>
                        </ListItemText>

                        <CustomCheckbox
                          checked={isSelected}
                          onChange={() => handleBuddyToggle(buddy)}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </Box>
          </Box>
        </Box>

        <Box className={styles.modalFooter}>
          <CustomButton
            variant="outlined"
            onClick={onClose}
          >
            Cancel
          </CustomButton>

          <CustomButton
            variant="contained"
            onClick={handleAddBuddies}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Adding...' : 'Add Buddies to Trip'}
          </CustomButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddTravelBuddiesModal;
